import { useEffect, useRef, useState, useCallback } from 'react';
import { Info, AlertTriangle, AlertCircle, Filter, ArrowDown, Pause, Play, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { MigrationLog, MigrationLogLevel } from '@/types';

interface LogStreamProps {
  logs: MigrationLog[];
  moduleKeys?: string[] | undefined;
  maxHeight?: string | undefined;
  className?: string | undefined;
}

const levelConfig: Record<MigrationLogLevel, {
  icon: React.ElementType;
  className: string;
  bgClassName: string;
}> = {
  Info: {
    icon: Info,
    className: 'text-muted-foreground',
    bgClassName: 'bg-muted/30',
  },
  Warning: {
    icon: AlertTriangle,
    className: 'text-warning',
    bgClassName: 'bg-warning/10',
  },
  Error: {
    icon: AlertCircle,
    className: 'text-destructive',
    bgClassName: 'bg-destructive/10',
  },
};

const REQUEST_BODY_PREFIX = 'Request body: ';

function tryParseRequestBody(message: string): string | null {
  if (!message.startsWith(REQUEST_BODY_PREFIX)) return null;
  const jsonPart = message.slice(REQUEST_BODY_PREFIX.length);
  try {
    return JSON.stringify(JSON.parse(jsonPart), null, 2);
  } catch {
    return null;
  }
}

interface LogEntryProps {
  log: MigrationLog;
  index: number;
  config: typeof levelConfig[MigrationLogLevel];
}

function LogEntry({ log, index, config }: LogEntryProps) {
  const [expanded, setExpanded] = useState(false);
  const Icon = config.icon;
  const time = new Date(log.timestamp);
  const prettyJson = tryParseRequestBody(log.message);

  if (prettyJson !== null) {
    return (
      <div
        className={cn('px-3 py-1.5 hover:bg-muted/50', config.bgClassName)}
      >
        <div
          className="flex gap-2 cursor-pointer select-none"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-muted-foreground shrink-0 w-20">
            {time.toLocaleTimeString()}
          </span>
          <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', config.className)} />
          {log.moduleKey && (
            <span className="text-primary shrink-0">[{log.moduleKey}]</span>
          )}
          <span className={cn('flex items-center gap-1', config.className)}>
            {expanded
              ? <ChevronDown className="h-3 w-3 shrink-0" />
              : <ChevronRight className="h-3 w-3 shrink-0" />}
            Request body
          </span>
        </div>
        {expanded && (
          <pre className={cn(
            'mt-1 ml-[6.5rem] overflow-x-auto rounded border border-border/50 bg-background/60 p-2 text-xs leading-relaxed',
            config.className
          )}>
            {prettyJson}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div
      key={log.id ?? index}
      className={cn(
        'flex gap-2 px-3 py-1.5 hover:bg-muted/50',
        config.bgClassName
      )}
    >
      <span className="text-muted-foreground shrink-0 w-20">
        {time.toLocaleTimeString()}
      </span>
      <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', config.className)} />
      {log.moduleKey && (
        <span className="text-primary shrink-0">[{log.moduleKey}]</span>
      )}
      <span className={cn('break-all', config.className)}>{log.message}</span>
    </div>
  );
}

export function LogStream({
  logs,
  moduleKeys = [],
  maxHeight = '400px',
  className,
}: LogStreamProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [selectedLevels, setSelectedLevels] = useState<Set<MigrationLogLevel>>(
    new Set(['Info', 'Warning', 'Error'])
  );
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Handle scroll to detect if user scrolled up
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  }, []);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setAutoScroll(true);
    }
  };

  const toggleLevel = (level: MigrationLogLevel) => {
    setSelectedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const toggleModule = (moduleKey: string) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleKey)) {
        next.delete(moduleKey);
      } else {
        next.add(moduleKey);
      }
      return next;
    });
  };

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    if (!selectedLevels.has(log.level)) return false;
    if (selectedModules.size > 0 && log.moduleKey && !selectedModules.has(log.moduleKey)) {
      return false;
    }
    return true;
  });

  const errorCount = logs.filter((l) => l.level === 'Error').length;
  const warningCount = logs.filter((l) => l.level === 'Warning').length;

  return (
    <div className={cn('rounded-lg border bg-muted/30', className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">Log Stream</span>
          <Badge variant="secondary" className="text-xs">
            {filteredLogs.length} / {logs.length}
          </Badge>
          {errorCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {errorCount} errors
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge variant="outline" className="text-xs text-warning">
              {warningCount} warnings
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Filter dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 px-2">
                <Filter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Log Levels</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={selectedLevels.has('Info')}
                onCheckedChange={() => toggleLevel('Info')}
              >
                <Info className="mr-2 h-4 w-4 text-muted-foreground" />
                Info
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={selectedLevels.has('Warning')}
                onCheckedChange={() => toggleLevel('Warning')}
              >
                <AlertTriangle className="mr-2 h-4 w-4 text-warning" />
                Warning
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={selectedLevels.has('Error')}
                onCheckedChange={() => toggleLevel('Error')}
              >
                <AlertCircle className="mr-2 h-4 w-4 text-destructive" />
                Error
              </DropdownMenuCheckboxItem>

              {moduleKeys.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Modules</DropdownMenuLabel>
                  {moduleKeys.map((key) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={selectedModules.size === 0 || selectedModules.has(key)}
                      onCheckedChange={() => toggleModule(key)}
                    >
                      {key}
                    </DropdownMenuCheckboxItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Auto-scroll toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setAutoScroll(!autoScroll)}
            title={autoScroll ? 'Pause auto-scroll' : 'Resume auto-scroll'}
          >
            {autoScroll ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>

          {/* Scroll to bottom */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={scrollToBottom}
            title="Scroll to bottom"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Log entries */}
      <div
        ref={containerRef}
        className="overflow-y-auto font-mono text-xs"
        style={{ maxHeight }}
        onScroll={handleScroll}
      >
        {filteredLogs.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            No logs to display
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {filteredLogs.map((log, index) => (
              <LogEntry
                key={log.id ?? index}
                log={log}
                index={index}
                config={levelConfig[log.level]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
